/* eslint-disable react/no-unused-state */
import * as React from 'react';
import Media from 'react-media';
import { Waypoint } from 'react-waypoint';
import { get, getText, isNumber, isBoolean, getConfig, validInternalUser, isAdminUser } from '../../helpers';
import Breadcrumb from '../../components/breadcrumb';
import DatasetIntroductionSection from '../../components/dataset-introduction-section';
import RequestTrialButton from '../../components/request-a-trial-button';
import RequestThisvisualizationButton from '../../components/request-this-visualization-button';
import SubNavigation from '../../components/sub-nav';
import APIManager from '../../utilities/api-manager';
import buildImageUrl from '../../utilities/build-image-url';
import queryStringManager from '../../utilities/query-string-manager';
import smoothScroll from '../../utilities/smooth-scroll';
import HelmetManager from '../../components/_common/helmet-manager';
import Loader from '../../components/_common/loader';
import PageLoaded from '../../components/_common/page-loaded';
import SPGVariables from '../../utilities/spg-variables';
import VisualizationAuth from '../../components/_common/visualization-auth';
import DatasetDataSection from '../../components/dataset-data-section'; /* webpackChunkName: "dataset-data-section" */
import DatasetVisualizationSection from '../../components/dataset-visualization-section'; /* webpackChunkName: "dataset-visualization-section" */
import DatasetNotebookSection from '../../components/dataset-notebook-section'; /* webpackChunkName: "dataset-notebook-section" */
import DatasetOverviewSection from '../../components/dataset-overview-section'; /* webpackChunkName: "dataset-overview-section" */
import RelatedDatasets from '../../components/section-related-datasets'; /* webpackChunkName: "section-related-datasets" */

const metaDataText = getText('pages.dataset.metaData');
const vizTitle = getText('generic.requestThisVisualization');
const spgVariables = SPGVariables();
let isUserOriginEnabled = false;
if (spgVariables.ENV !== 'dev') {
    isUserOriginEnabled = getConfig('responseHeader.isResHeaderEnabled');
}

export default class Dataset extends React.Component<PageProps & DatasetProps & DatasetActions & FeatureFlags, DatasetState> {
    public ratButtonElement: React.RefObject<HTMLDivElement>;

    public datasetWrapperElement: React.RefObject<HTMLDivElement>;

    public subNavItems = getText('pages.dataset.subNavItems');

    public constructor(props: PageProps & DatasetProps & DatasetActions) {
        super(props);
        const { dataProfile } = props;

        this.ratButtonElement = React.createRef();
        this.datasetWrapperElement = React.createRef();

        this.state = {
            sticky: false,
            showRatButton: true,
            SampleDataFilePath: '',
            DataDictionaryFilePath: '',
            id: (dataProfile && dataProfile.id) || 0,
            productAccessLinks: [],
            reqHeader: ''
        };
    }

    public componentDidMount() {
        const { isBundle, id, user, dataProfile, fetchFullDataProfile, fflagEnableWorkbench } = this.props;
        const isValidUser = validInternalUser(user);

        const noCache = isAdminUser(user.userInfo && user.userInfo.Email);

        if ((id && !dataProfile) || (id && noCache)) {
            fetchFullDataProfile({ id: id.toString(), isBundle });
        }

        if (user.isLoggedIn && id) {
            this.fetchDownloadLink(id, isBundle);
            if (user.userInfo && isValidUser && fflagEnableWorkbench) {
                APIManager.getDatasetAccessLinksbyKey(isBundle ? 'bundle' : 'dataset', id.toString()).then(response => {
                    if (response.data.length > 0) this.setState({ productAccessLinks: response.data });
                });
            }
        }

        const userOriginCountryCodeFromSession = sessionStorage.getItem('userOriginCountryCode');
        if (userOriginCountryCodeFromSession === null) {
            APIManager.getReqHeaderByName('PMUSER_COUNTRY_CODE').then(response => {
                this.setState({
                    reqHeader: response.headers.userorigincountrycode || ''
                });
                sessionStorage.setItem('userOriginCountryCode', response.headers.userorigincountrycode);
            });
        } else {
            this.setState({
                reqHeader: userOriginCountryCodeFromSession
            });
        }

        window.addEventListener('scroll', this.setStickyRATButton, true);
    }

    public componentDidUpdate(prevProps: PageProps & DatasetProps & DatasetActions & FeatureFlags) {
        const { id, fetchFullDataProfile, isBundle, user, fflagEnableWorkbench } = this.props;
        const isValidUser = validInternalUser(user);
        if (!id) return;
        const hasIdChanged = prevProps.id !== id;
        if (hasIdChanged) {
            fetchFullDataProfile({ id: id.toString(), isBundle: !!isBundle });
            if (user.isLoggedIn) {
                this.fetchDownloadLink(id, isBundle);
            }
        }
        const acceelinksVisible = user.userInfo && isValidUser && fflagEnableWorkbench;
        const acceeLinksFlagChanged = prevProps.fflagEnableWorkbench !== fflagEnableWorkbench;
        if (acceelinksVisible && (prevProps.user.userInfo === undefined || hasIdChanged || acceeLinksFlagChanged)) {
            APIManager.getDatasetAccessLinksbyKey(isBundle ? 'bundle' : 'dataset', id.toString()).then(response => {
                if (response.data.length > 0) this.setState({ productAccessLinks: response.data });
            });
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('scroll', this.setStickyRATButton, true);
    }

    public handleBlur = (w: Waypoint.CallbackArgs, hash = '') => {
        const focusedElement = document.activeElement;
        if (focusedElement && focusedElement.tagName === 'A') {
            const focusedAnchorElement = (focusedElement as HTMLAnchorElement);
            if (hash === focusedAnchorElement.hash) {
                focusedAnchorElement.blur();
            }
        }
    };

    public handleScroll = (w: Waypoint.CallbackArgs, hash = '') => {
        if (!w.previousPosition || w.viewportBottom < w.waypointTop) return;
        queryStringManager.setHash(hash);
    };

    public scrollTo = (event: React.SyntheticEvent, hash: string) => {
        smoothScroll(hash, event);
        queryStringManager.setHash(hash);
        const activeSubNav = document.querySelector(`.sub-nav__item[href="${hash}"]`);
        if (activeSubNav) {
            activeSubNav.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    public fetchDownloadLink = (KeyMarketplaceDataset: number, IsBundle: boolean) => {
        APIManager.getDownloadLinkByKey(KeyMarketplaceDataset.toString(), IsBundle).then(response => {
            this.setState({
                id: IsBundle ? response.data.KeyMarketplaceDatasetBundle : response.data.KeyMarketplaceDataset,
                DataDictionaryFilePath: response.data.DataDictionaryFilePath || '',
                SampleDataFilePath: response.data.SampleDataFilePath || ''
            });
        });
    };

    public setTableauUrl = (dataVisualization: MarketDatasetVisualization[]) => {
        const dataVisualizations: MarketDatasetVisualization[] = [];
        dataVisualization.forEach(viz => {
            let MktplaceDataVisualizationURL;
            if (viz.VisualizationType === 'TABLEAU') {
                if (viz.MktplaceShowcaseVisualization) {
                    MktplaceDataVisualizationURL = `t/${spgVariables.TABLEAU_PUBLICSITE}/views/${viz.MktplaceDataVisualizationURL}`;
                } else {
                    MktplaceDataVisualizationURL = `t/${spgVariables.TABLEAU_SECURESITE}/views/${viz.MktplaceDataVisualizationURL}`;
                }
            } else {
                MktplaceDataVisualizationURL = viz.MktplaceDataVisualizationURL;
            }
            dataVisualizations.push({ ...viz, MktplaceDataVisualizationURL });
        });
        return dataVisualizations;
    };

    public setStickyRATButton = () => {
        if (
            this.ratButtonElement.current &&
            this.ratButtonElement.current.parentElement &&
            this.datasetWrapperElement &&
            this.datasetWrapperElement.current
        ) {
            const ratButtonRect = this.ratButtonElement.current.parentElement.getBoundingClientRect();
            const datasetWrapperRect = this.datasetWrapperElement.current.getBoundingClientRect();

            // Using top of screen as marker, calculate how far for the bottom of the button to reach the bottom of the screen:
            // 55 is height of button
            const buttonPastBottom = ratButtonRect.top + 55 - window.innerHeight;
            // We use the bottom of the dataset wrapper to check if the footer is coming into view
            const datasetPastBottom = datasetWrapperRect.top + datasetWrapperRect.height - window.innerHeight;
            // Make sure it doesn't work for desktop sizes
            const isDesktop = window.innerWidth >= 1024;

            //  Hide the button completely when footer is in view (avoids jumping issue)
            //  Set to sticky once the button has passed the screen by 20px, for seamless transition
            if (datasetPastBottom < -20 && !isDesktop) {
                // hide the button when the footer comes into view
                this.setState({
                    sticky: true,
                    showRatButton: false
                });
            } else if (buttonPastBottom < -20 && !isDesktop) {
                //  Make sticky once button has fully entered view
                this.setState({
                    sticky: true,
                    showRatButton: true
                });
            } else {
                this.setState({
                    sticky: false,
                    showRatButton: true
                });
            }
        }
    };

    private hasOverview = (dataProfile: DataProfile) => {
        return (
            (dataProfile.marketDatasetMIIndustrys && dataProfile.marketDatasetMIIndustrys.length > 0) ||
            (dataProfile.marketDatasetGeographys && dataProfile.marketDatasetGeographys.length > 0) ||
            (dataProfile.deliveryChannel && dataProfile.deliveryChannel.length > 0) ||
            (dataProfile.deliveryPlatform && dataProfile.deliveryPlatform.length > 0) ||
            !!dataProfile.spgCoverageType ||
            !!(
                dataProfile.marketplaceDatasetCoverageDesc ||
                isNumber(dataProfile.earliestRecord) ||
                isNumber(dataProfile.earliestSignificantCoverage) ||
                isBoolean(dataProfile.pointInTime) ||
                !!dataProfile.pointInTimeDescription ||
                !!dataProfile.marketplaceDataSource ||
                !!dataProfile.marketplaceDatasetFieldCount ||
                !!dataProfile.spgDatasetFrequency ||
                !!dataProfile.spgDatasetLatency ||
                isNumber(dataProfile.estimatedDatasetSize)
            )
        );
    };

    private hideSubnavItems(dataProfile: DataProfile) {
        const datasetBundle = dataProfile.bundleDatasets || [dataProfile];
        const { reqHeader } = this.state;
        const hasDataDictionaryLinks = datasetBundle.map(dataset => dataset.dataDictionaryLink).filter(item => item !== null).length > 0;
        const hasSampleDataLinks = datasetBundle.map(dataset => dataset.sampleDataLink).filter(item => item !== null).length > 0;
        const hasNotebooks = dataProfile.marketDatasetNotebooks && dataProfile.marketDatasetNotebooks.length > 0;
        const hasDataVisualizationHrefs = dataProfile.marketDatasetVisualization && dataProfile.marketDatasetVisualization.length > 0;
        const hasRelatedDatasets = dataProfile.marketDatasetDatasets && dataProfile.marketDatasetDatasets.length > 0;
        this.subNavItems.forEach((item: AnonymousObject) => {
            item.enabled = false;
            if (isUserOriginEnabled && reqHeader.length === 0) {
                item.enabled = false;
            } else {
                if (item.id === 0) {
                    item.enabled = this.hasOverview(dataProfile);
                }
                if (item.id === 1) {
                    if (reqHeader === 'CN') {
                        item.enabled = false;
                    } else {
                        item.enabled = hasDataVisualizationHrefs;
                    }
                }
                if (item.id === 2) {
                    item.enabled = hasNotebooks;
                }
                if (item.id === 3) {
                    if (reqHeader === 'CN') {
                        item.enabled = false;
                    } else {
                        item.enabled = hasSampleDataLinks;
                    }
                }
                if (item.id === 4) {
                    if (reqHeader === 'CN') {
                        item.enabled = false;
                    } else {
                        item.enabled = hasDataDictionaryLinks;
                    }
                }
                if (item.id === 5) {
                    item.enabled = hasRelatedDatasets;
                }
            }
        });
    }

    public render() {
        const { dataProfile, user, keyPage, startDate, toggleSessionCreate, solutionurl } = this.props;
        if (!dataProfile) return <Loader minHeight="80vh" />;
        const isPlatts = dataProfile.dataProvider && dataProfile.dataProvider.toLowerCase().indexOf('platts') > -1;

        const { sticky, showRatButton, DataDictionaryFilePath, SampleDataFilePath, productAccessLinks } = this.state;
        const dataVisualization =
            dataProfile.marketDatasetVisualization !== null ? this.setTableauUrl(dataProfile.marketDatasetVisualization || []) : [];
        const hasSecureVisual = dataVisualization.filter(visual => visual.MktplaceShowcaseVisualization === false).length > 0;
        const isValidUser = validInternalUser(user);
        const dataSetId = get(this.props, 'match.params.id', 'no id found');
        const url = dataProfile.isBundle ? `/datasets/bundle/${dataSetId}` : `/datasets/${dataSetId}`;
        const metaData = {
            title: `${dataProfile.name} ${metaDataText.subTitle}`,
            description: dataProfile.shortDescription,
            type: 'Dataset',
            url: `${url}`,
            image: buildImageUrl(dataProfile.socialSharingImageFilePath)
        };

        const breadCrumbData = [
            { text: getText('generic.datasets'), url: '/datasets' },
            { text: (dataProfile && dataProfile.name) || '', url }
        ];

        const { reqHeader } = this.state;

        const supportLinks = [];
        if (dataProfile.supportDocs) {
            supportLinks.push(
                ...dataProfile.supportDocs
                    .sort((a, b) => {
                        return a.FormOrder - b.FormOrder;
                    })
                    .map(item => {
                        return {
                            text: `${item.MarketPlaceSupportDoc.MarketPlaceSupportDocument}`,
                            type: 'externalLink',
                            url: item.MarketPlaceSupportDoc.WebSiteURL
                        };
                    })
            );
        }
        if (dataProfile.mktplaceSolutionSupportEmail) {
            supportLinks.push({
                text: dataProfile.mktplaceSolutionSupportEmail,
                type: 'emailAddress',
                url: `mailto:${dataProfile.mktplaceSolutionSupportEmail}`
            });
        } else if (isPlatts) {
            supportLinks.push({
                text: getText('generic.supportPlattsEmail'),
                type: 'emailAddress',
                url: `mailto:${getText('generic.supportPlattsEmail')}`
            });
        } else {
            supportLinks.push({ text: getText('generic.supportEmail'), type: 'emailAddress', url: `mailto:${getText('generic.supportEmail')}` });
        }
        let researchLinks: LinkListGroupItem[] | [] = [];

        const researchData =
            dataProfile.marketDatasetResearchs &&
            dataProfile.marketDatasetResearchs.sort((a, b) => {
                return a.FormOrder > b.FormOrder ? 1 : -1;
            });

        if (researchData) {
            researchLinks = researchData.map(researchDetails => {
                const linkListGroupItem: LinkListGroupItem = {
                    text: researchDetails.DatasetResearchTitle,
                    type: '',
                    url: researchDetails.WebSiteURL
                };
                return linkListGroupItem;
            });
        } else {
            researchLinks = [];
        }

        const internalResourceLinks: LinkListGroupItem[] = [];
        if (productAccessLinks.length > 0) {
            productAccessLinks
                .filter(x => x.KeyMarketplaceDataset === dataProfile.id || x.KeyMarketplaceDatasetBundle === dataProfile.id)
                .sort((a, b) => a.FormOrder - b.FormOrder)
                .forEach(item => {
                    internalResourceLinks.push({
                        text: item.MarketplaceAccessLinkTitle,
                        type: 'externalLink',
                        url: item.WebSiteURL
                    });
                });
        }
        const datasetBundle = dataProfile.bundleDatasets || [];
        const sampleDataLinkId = dataProfile.sampleDataLink || '';
        const dataDictionaryLinkId = dataProfile.dataDictionaryLink || '';
        this.hideSubnavItems(dataProfile);
        // console.log('dataset:metaData:', metaData);

        return (
            <article className="page dataset" ref={this.datasetWrapperElement}>
                <HelmetManager metaData={metaData} />
                {hasSecureVisual && (
                    <VisualizationAuth
                        toggleSessionCreate={toggleSessionCreate}
                        isTableauSessionCreated={user.isTableauSessionCreated || false}
                        isLoggedIn={user.isLoggedIn}
                    />
                )}
                <Breadcrumb pathData={breadCrumbData} sanitizeSuperScript noMargin />
                <Waypoint topOffset="40%" bottomOffset="50%" onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w)}>
                    <div>
                        <DatasetIntroductionSection
                            dataProfile={dataProfile}
                            supportLinks={supportLinks}
                            internalResourceLinks={internalResourceLinks}
                            researchLinks={researchLinks}
                            isLoggedIn={user.isLoggedIn}
                            ratButtonElement={this.ratButtonElement}
                            sticky={sticky}
                            showRatButton={showRatButton}
                            isBundle={dataProfile.isBundle}
                            isInternal={isValidUser}
                            datasetId={dataSetId}
                        />
                    </div>
                </Waypoint>
                <div className="dataset__content">
                    {(!isUserOriginEnabled || reqHeader.length > 0) && (
                        <SubNavigation navItems={this.subNavItems.filter((item: AnonymousObject) => item.enabled)} />
                    )}
                    {this.subNavItems[0].enabled && (
                        <Waypoint
                            topOffset="40%"
                            bottomOffset="50%"
                            onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w, this.subNavItems[0].href)}
                            onLeave={(w: Waypoint.CallbackArgs) => this.handleBlur(w, this.subNavItems[0].href)}
                        >
                            <div>
                                <DatasetOverviewSection
                                    dataset={dataProfile}
                                    scrollTo={event => this.scrollTo(event, this.subNavItems[0].href)}
                                    isBundle={dataProfile.isBundle}
                                />
                            </div>
                        </Waypoint>
                    )}
                    <hr />
                    {this.subNavItems[1].enabled && (
                        <div className="btn_align">
                            <RequestThisvisualizationButton
                                buttonText={vizTitle}
                                productName={dataProfile.name}
                                isLoggedIn={user.isLoggedIn}
                                dataProvider="Visualization"
                                productVariant="dataset"
                                metaData={metaData}
                            />
                        </div>
                    )}
                    {this.subNavItems[1].enabled && (
                        <Waypoint
                            topOffset="40%"
                            bottomOffset="50%"
                            onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w, this.subNavItems[1].href)}
                            onLeave={(w: Waypoint.CallbackArgs) => this.handleBlur(w, this.subNavItems[1].href)}
                        >
                            <div>
                                <DatasetVisualizationSection
                                    isTableauSessionCreated={user.isTableauSessionCreated || false}
                                    isBundle={dataProfile.isBundle}
                                    isLoggedIn={user.isLoggedIn}
                                    scrollTo={event => this.scrollTo(event, this.subNavItems[1].href)}
                                    dataSectionType="data-visualization"
                                    dataVisualization={dataVisualization}
                                />
                                <hr />
                            </div>
                        </Waypoint>
                    )}
                    {this.subNavItems[2].enabled && (
                        <Waypoint
                            topOffset="40%"
                            bottomOffset="50%"
                            onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w, this.subNavItems[2].href)}
                            onLeave={(w: Waypoint.CallbackArgs) => this.handleBlur(w, this.subNavItems[2].href)}
                        >
                            <div>
                                <DatasetNotebookSection
                                    isBundle={dataProfile.isBundle}
                                    isLoggedIn={user.isLoggedIn}
                                    scrollTo={event => this.scrollTo(event, this.subNavItems[2].href)}
                                    dataSectionType="data-notebooks"
                                    dataNotebooks={dataProfile.marketDatasetNotebooks}
                                    solutionurl={solutionurl}
                                />
                                <hr />
                            </div>
                        </Waypoint>
                    )}

                    {this.subNavItems[3].enabled && (
                        <Waypoint
                            topOffset="40%"
                            bottomOffset="50%"
                            onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w, this.subNavItems[3].href)}
                            onLeave={(w: Waypoint.CallbackArgs) => this.handleBlur(w, this.subNavItems[3].href)}
                        >
                            <div>
                                <DatasetDataSection
                                    dataFilePath={SampleDataFilePath}
                                    dataId={sampleDataLinkId}
                                    datasetBundle={datasetBundle}
                                    isBundle={dataProfile.isBundle}
                                    isLoggedIn={user.isLoggedIn}
                                    scrollTo={event => this.scrollTo(event, this.subNavItems[3].href)}
                                    dataGridType="sample-data"
                                />
                                <hr />
                            </div>
                        </Waypoint>
                    )}

                    {this.subNavItems[4].enabled && (
                        <Waypoint
                            topOffset="40%"
                            bottomOffset="50%"
                            onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w, this.subNavItems[4].href)}
                            onLeave={(w: Waypoint.CallbackArgs) => this.handleBlur(w, this.subNavItems[4].href)}
                        >
                            <div>
                                <DatasetDataSection
                                    dataFilePath={DataDictionaryFilePath}
                                    dataId={dataDictionaryLinkId}
                                    datasetBundle={datasetBundle}
                                    isBundle={dataProfile.isBundle}
                                    isLoggedIn={user.isLoggedIn}
                                    scrollTo={event => this.scrollTo(event, this.subNavItems[4].href)}
                                    dataGridType="data-dictionary"
                                />
                                <hr />
                            </div>
                        </Waypoint>
                    )}
                </div>

                <Waypoint topOffset="40%" bottomOffset="50%" onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w, this.subNavItems[5].href)} onLeave={(w: Waypoint.CallbackArgs) => this.handleBlur(w, this.subNavItems[5].href)}>
                    <div>
                        {this.subNavItems[5].enabled && (
                            <RelatedDatasets
                                scrollTo={event => this.scrollTo(event, this.subNavItems[5].href)}
                                className="dataset__related-items"
                                dataProfile={dataProfile}
                            />
                        )}
                        <Media query={{ minWidth: 1024 }}>
                            <div className="page-section__inner">
                                <RequestTrialButton
                                    buttonText={dataProfile.marketplaceTrialButtonTitle}
                                    productName={dataProfile.name}
                                    productId={dataProfile.id.toString()}
                                    isLoggedIn={user.isLoggedIn}
                                    dataProvider={dataProfile.dataProvider}
                                    productVariant="dataset"
                                    isXLButton
                                    dataFilePath={dataProfile.marketMSADataFilePath}
                                    marketMSADataLink={dataProfile.marketMSADataLink}
                                    marketplaceMSATooltip={dataProfile.marketplaceMSATooltip}
                                    metaData={metaData}
                                    spgCoverageType={get(dataProfile.spgCoverageType, 'SPGCoverageType')}
                                />
                            </div>
                        </Media>
                    </div>
                </Waypoint>

                {dataProfile.longDescription && <PageLoaded keyPage={keyPage} startDate={startDate} />}
            </article>
        );
    }
}
